import { Box, Button } from "@mui/material";
import React, { useState } from "react";

import OTPInput from "@/components/common/input/OtpInput";
import SecurityWarning from "@/components/common/multi-factor/SecurityWarning";
import {
  authFormContainer,
  authFormSections,
  authHeaderSection,
  authSubmitButton,
  authSubtitle,
  authTitle,
} from "@/components/page-auth/auth-page-styles";

const OTP_LENGTH = 6;

type Props = {
  /** 認証コードの検証。成功で true を返すこと（false の間は入力画面に留まる） */
  onVerify: (code: string) => Promise<boolean>;
  /** 「はじめからやり直す」。保留中の認証状態を捨てて元の画面へ戻す */
  onRestart: () => void;
  disabled?: boolean;
};

/**
 * ログイン・招待受諾・再認証で共通の認証コード入力。
 *
 * SMS を送れたときにだけ表示する。送れていないのにこの画面を出すと、
 * 届かないコードを待つ行き止まりになる。
 */
const OtpForm: React.FC<Props> = ({ onVerify, onRestart, disabled }) => {
  const [code, setCode] = useState("");
  const [error, setError] = useState(false);
  // 検証中の再送信を止める。同じ resolver へ resolveSignIn を並行で投げると挙動が保証されず、
  // 片方が失敗して「認証に失敗しました」だけが残ることがある（Enter 連打で起きる）
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting || code.length !== OTP_LENGTH) return;

    setSubmitting(true);
    try {
      const ok = await onVerify(code);
      if (!ok) {
        setError(true);
        setCode("");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box css={authFormContainer}>
      <Box component="form" css={authFormSections} onSubmit={onSubmit}>
        <Box css={authHeaderSection}>
          <Box css={authTitle} pb={1}>
            認証コードの入力
          </Box>
          <Box css={authSubtitle}>登録されている電話番号へ認証コードを記したSMSをお送りしました。</Box>
        </Box>

        <Box>
          <OTPInput
            length={OTP_LENGTH}
            value={code}
            onChange={(v) => {
              setCode(v);
              setError(false);
            }}
          />
          {error && <SecurityWarning />}
        </Box>

        <Box>
          <Button
            css={authSubmitButton}
            type="submit"
            fullWidth
            variant="contained"
            size="large"
            disabled={disabled || submitting || code.length !== OTP_LENGTH}
          >
            認証する
          </Button>
          <Box pt={2} textAlign="center">
            <Button variant="text" onClick={onRestart} disabled={disabled || submitting}>
              はじめからやり直す
            </Button>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default React.memo(OtpForm);
